import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { Agent } from '../../types/domain';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import { includes } from '../../utils/array';

type UseAgentsData = {
  isLoading: boolean;
  agents: Agent[];
};

const EMPTY_DATA: Agent[] = [];

const useAgentsData = (): UseAgentsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();

  const isRequired =
    includes(['home', 'work'], aggregationConfig?.dimension) &&
    includes(['grid_heatmap', 'heatmap'], aggregationConfig?.type);

  const { data, isFetching } = useQuery({
    queryKey: ['agents', currentSimDataId, apiFilter],
    queryFn:
      currentSimDataId && isRequired
        ? async ({ signal }: QueryFunctionContext): Promise<Agent[]> => {
            return dataApiClient.getAgents(currentSimDataId, apiFilter, signal);
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'aggregation' },
  });

  return {
    isLoading: isFetching,
    agents: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useAgentsData };
